import { type SanityImageSource } from "@sanity/image-url/lib/types/types";
import { groq } from "next-sanity";
import { handleClientError } from "../../errors";
import { getClient } from "../sanity";
import { type BlogSimple, blogSimpleFragment } from "./blogs";
import { type PlaybookSimple, playbookSimpleFragment } from "./playbooks";

/**
 * Integration categories that are confirmed to work with the DestinationMarquee with only that category included.
 * Check new categories before adding them to the list - if they have too few items, the marquee looks broken.
 */
export type IntegrationCategory = "all" | "Advertising" | "E-commerce";

export type IntegrationSimple = {
  name: string;
  icon: SanityImageSource;
  iconUrl: string;
  slug: string;
  vendorUrl?: string;
};

export const integrationSimpleFragment = "name, slug, vendorUrl, 'iconUrl': icon.asset->url";

export const getSimpleIntegrations = async (
  type: "source" | "destination",
  category: IntegrationCategory = "all",
): Promise<IntegrationSimple[]> => {
  let query = groq`
    *[_type == "integration" && type == $type && status == "released"`;

  if (category !== "all") {
    query += groq` && $category in categories[]->name`;
  }

  query += groq`] {
      ${integrationSimpleFragment}
    }
  `;

  try {
    const parameters = { type, category };
    const integrations = await getClient().fetch<IntegrationSimple[]>(query, parameters);
    return integrations;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    handleClientError(`Failed to fetch ${type} integrations for category "${category}": ${errorMessage}`);
    return [];
  }
};

export type Integration = {
  name: string;
  slug: string;
  type: string;
  status: string;
  icon: SanityImageSource;
  shortPitch?: string;
  features?: string[];
  vendorAbout?: string;
  vendorUrl?: string;
  iconUrl: string;
  categories?: Array<{
    name: string;
    shortDescription?: string;
  }>;
  supportsInWarehouseDiffing?: boolean;
  supportedQueryTypes?: string[];
};

const integrationFragment =
  "name, slug, type, status, icon, shortPitch, features, vendorAbout, vendorUrl, 'iconUrl': icon.asset->url, 'categories': categories[]->{name, shortDescription}, supportsInWarehouseDiffing, supportedQueryTypes";

export const getIntegration = async (
  slug: string,
  type?: "source" | "destination",
): Promise<Integration | undefined> => {
  let query = groq`
    *[_type == "integration" && 
      slug == $slug &&
      (status == "released" || ((status == "alpha" || status == "coming_soon") && shouldGenerateWaitlistLandingPage == true))`;

  if (type) {
    query += groq` && type == $type`;
  }

  query += groq`] {
      ${integrationFragment}
    }
  `;

  try {
    const parameters = { slug, type };
    const integration = await getClient().fetch(query, parameters);
    return integration?.[0] ?? undefined;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    handleClientError(
      `Failed to fetch integration with slug "${slug}"${type ? ` and type "${type}"` : ""}: ${errorMessage}`,
    );
    return undefined;
  }
};

export type JointIntegration = {
  name: string;
  slug: string;
  jointPitch: string;
  seoSectionTitle?: string;
  seoSectionContent?: string;
  metaTitle?: string;
  metaDescription?: string;
  source: Integration;
  destination: Integration;
  relatedBlogs?: BlogSimple[];
  relatedPlaybooks?: PlaybookSimple[];
};

export const getJointIntegration = async (jointSlug: string): Promise<JointIntegration | undefined> => {
  const query = groq`
    *[_type == "jointIntegration" && slug == $jointSlug] {
      name,
      slug,
      jointPitch,
      seoSectionTitle,
      seoSectionContent,
      metaTitle,
      metaDescription,
      'source': source->{
        ${integrationFragment}
      },
      'destination': destination->{
        ${integrationFragment}
      },
      'relatedBlogs': relatedBlogs[]->{
        ${blogSimpleFragment}
      },
      'relatedPlaybooks': relatedPlaybooks[]->{
        ${playbookSimpleFragment}
      },
    }
  `;

  try {
    const parameters = { jointSlug };
    const jointIntegration = await getClient().fetch(query, parameters);
    return jointIntegration?.[0] ?? undefined;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    handleClientError(`Failed to fetch joint integration with slug "${jointSlug}": ${errorMessage}`);
    return undefined;
  }
};
